import ReportsOfSicknessUtils from '@/resources/utils/reports-of-sickness';
import PeriodUtils from '@/resources/utils/period';
import RecoveryUtils from '@/resources/utils/recovery';
import UwvDecisionUtils from '@/resources/utils/uwv-decision';

import States from '@/resources/models/states';
import ReportsOfSickness from '@/resources/constants/reports-of-sickness';
import { ReportOfSickness } from '@/resources/models/reports-of-sickness';

import NewReportOfSickness from '@/views/constants/report-of-sickness-new';
import ReportsOfSicknessView from '@/views/constants/reports-of-sickness';

import App from '@/views/constants/app';

const state = {
  /* reportsOfSickness:
     reportsOfSickness inserted into Object based on employeeId (key):
     reportsOfSickness: { employeeId: [], employeeId: [], .. , ..};
     this makes for easier retrieval and insertion or updates. Also more
     advantageous for performance in the long run */
  reportsOfSickness: { },
  reportsOfSicknessLoading: {
    'LOAD': false,
    'LOAD_ALL': false,
    'LOAD_FOR_EMPLOYEE': false,
    'CREATE': false,
    'UPDATE': false,
    'DELETE': false,
    'MARK_RECOVERED': false,
    'UNDO_MARK_RECOVERED': false,
    'PERIOD_CREATE': false,
    'PERIOD_DELETE': false,
    'PERIOD_UPDATE': false,
    'RECOVERY_CREATE': false,
    'RECOVERY_DELETE': false,
    'RECOVERY_UPDATE': false,
    'UWV_DECISION_CREATE': false
  }
};

const getters = {
  reportsOfSickness: state => state.reportsOfSickness,

  reportsOfSicknessFlat: state => {
    let result = [];
    for (let employeeId in state.reportsOfSickness) {
      state.reportsOfSickness[employeeId].forEach(rs => {
        result.push(rs);
      });
    }
    return result;
  },

  isCreateReportOfSicknessLoading: state => state.reportsOfSicknessLoading['CREATE'],
  isUpdateReportOfSicknessLoading: state => state.reportsOfSicknessLoading['UPDATE'],
  isDeleteReportOfSicknessLoading: state => state.reportsOfSicknessLoading['DELETE'],
  isReportsOfSicknessForEmployeeLoading: state => state.reportsOfSicknessLoading['LOAD_FOR_EMPLOYEE'],
  isCreatePeriodLoading: state => state.reportsOfSicknessLoading['PERIOD_CREATE'],
  isUpdatePeriodLoading: state => state.reportsOfSicknessLoading['PERIOD_UPDATE'],
  isDeletePeriodLoading: state => state.reportsOfSicknessLoading['PERIOD_DELETE'],
  isCreateRecoveryLoading: state => state.reportsOfSicknessLoading['RECOVERY_CREATE'],
  isUpdateRecoveryLoading: state => state.reportsOfSicknessLoading['RECOVERY_UPDATE'],
  isDeleteRecoveryLoading: state => state.reportsOfSicknessLoading['RECOVERY_DELETE'],

  isCreateUwvDecisionLoading: state => state.reportsOfSicknessLoading['UWV_DECISION_CREATE'],
  isReportsOfSicknessLoading: state => state.reportsOfSicknessLoading['LOAD_ALL'],

  isMarkRecoveredLoading: state => state.reportsOfSicknessLoading['MARK_RECOVERED'],
  isUndoMarkRecoveredLoading: state => state.reportsOfSicknessLoading['UNDO_MARK_RECOVERED']
};

const actions = {
  [ReportsOfSickness.Actions.Load.START] ({ commit }, id) {
    ReportsOfSicknessUtils.Load(id)
      .then(response => commit(ReportsOfSickness.Mutations.UPDATE, response.data));
  },

  [ReportsOfSickness.Actions.LoadAll.START] ({ commit }, employerId) {
    ReportsOfSicknessUtils.LoadAll(employerId)
      .then(response => commit(ReportsOfSickness.Mutations.SET, response.data));
  },

  [ReportsOfSickness.Actions.LoadForEmployee.START] ({ commit }, payload) {
    ReportsOfSicknessUtils.LoadForEmployee(payload.employerId, payload.employeeId)
      .then((response) => commit(ReportsOfSickness.Mutations.UPSERT, {
        data: response.data,
        employeeId: payload.employeeId
      }));
  },

  [ReportsOfSickness.Actions.Create.START] ({ commit, dispatch, rootGetters }, reportOfSickness) {
    // TO-DO: Rework, this is only for testing using multiple Employers
    reportOfSickness.employerId = rootGetters.selectedEmployer.id;

    ReportsOfSicknessUtils.Create(reportOfSickness)
      .then((response) => {
        commit(ReportsOfSickness.Mutations.ADD, response.data);
        dispatch(ReportsOfSickness.Actions.Create.SUCCESS, response.data);
      })
      .catch((error) => {
        dispatch(ReportsOfSickness.Actions.Create.FAILED, error)
          .then(dispatch(NewReportOfSickness.Actions.Steps.Completed.UPDATE))
          .then(dispatch(NewReportOfSickness.Actions.Navigate.DO, NewReportOfSickness.Views.NewReportOfSicknessForm));
      });
  },

  [ReportsOfSickness.Actions.Update.START] ({ commit, dispatch, rootGetters }, reportOfSickness) {
    ReportsOfSicknessUtils.Update(reportOfSickness)
      .then((response) => {
        if (reportOfSickness.id !== response.data.id) {
          dispatch(ReportsOfSicknessView.Actions.REFRESH, {
            newReportOfSickness: new ReportOfSickness(response.data),
            oldReportOfSickness: reportOfSickness
          });
        } else {
          commit(ReportsOfSickness.Mutations.UPDATE, response.data);
        }
        dispatch(ReportsOfSickness.Actions.Update.SUCCESS, response.data);
      })
      .catch((errors) => {
        dispatch(ReportsOfSickness.Actions.Update.FAILED, errors);
      });
  },

  [ReportsOfSickness.Actions.Delete.START] ({ commit, dispatch }, reportOfSickness) {
    ReportsOfSicknessUtils.Delete(reportOfSickness.id)
      .then(() => {
        commit(ReportsOfSickness.Mutations.REMOVE, reportOfSickness);
        dispatch(ReportsOfSickness.Actions.Delete.SUCCESS, reportOfSickness);
      })
      .catch(() => dispatch(ReportsOfSickness.Actions.Delete.FAILED));
  },

  [ReportsOfSickness.Actions.Period.Create.START] ({ commit, dispatch }, payload) {
    PeriodUtils.Create(payload.reportOfSickness.id, payload.period)
      .then((response) => {
        if (payload.reportOfSickness.id !== response.data.id) {
          dispatch(ReportsOfSicknessView.Actions.REFRESH, {
            newReportOfSickness: new ReportOfSickness(response.data),
            oldReportOfSickness: payload.reportOfSickness
          });
        } else {
          commit(ReportsOfSickness.Mutations.UPDATE, response.data);
        }
        dispatch(ReportsOfSickness.Actions.Period.Create.SUCCESS, response.data);
      });
  },

  [ReportsOfSickness.Actions.Period.Delete.START] ({ commit, dispatch }, data) {
    PeriodUtils.Delete(data.reportOfSickness.id, data.period)
      .then((response) => {
        dispatch(ReportsOfSickness.Actions.Period.Delete.SUCCESS, data);
        commit(ReportsOfSickness.Mutations.Period.REMOVE, data);
      });
  },

  [ReportsOfSickness.Actions.Period.Update.START] ({ commit, dispatch }, data) {
    const period = data.period;
    const reportOfSickness = data.reportOfSickness;

    PeriodUtils.Update(reportOfSickness.id, period)
      .then((response) => {
        if (reportOfSickness.id !== response.data.id) {
          dispatch(ReportsOfSicknessView.Actions.REFRESH, {
            newReportOfSickness: new ReportOfSickness(response.data),
            oldReportOfSickness: reportOfSickness
          });
        } else {
          commit(ReportsOfSickness.Mutations.UPDATE, response.data);
        }
        dispatch(ReportsOfSickness.Actions.Period.Update.SUCCESS, data);
      })
      .catch((errors) => {
        dispatch(ReportsOfSickness.Actions.Period.Update.FAILED);
      });
  },

  [ReportsOfSickness.Actions.Recovery.Create.START] ({ commit, dispatch }, payload) {
    RecoveryUtils.Create(payload.reportOfSickness.id, payload.recovery)
      .then((response) => {
        if (payload.reportOfSickness.id !== response.data.id) {
          dispatch(ReportsOfSicknessView.Actions.REFRESH, {
            newReportOfSickness: new ReportOfSickness(response.data),
            oldReportOfSickness: payload.reportOfSickness
          });
        } else {
          commit(ReportsOfSickness.Mutations.UPDATE, response.data);
        }
        dispatch(ReportsOfSickness.Actions.Recovery.Create.SUCCESS, response.data);
      })
      .catch(errors => dispatch(ReportsOfSickness.Actions.Recovery.Create.FAILED, errors));
  },

  [ReportsOfSickness.Actions.Recovery.Update.START] ({ commit, dispatch }, payload) {
    RecoveryUtils.Update(payload.reportOfSickness.id, payload.recovery)
      .then((response) => {
        if (payload.reportOfSickness.id !== response.data.id) {
          dispatch(ReportsOfSicknessView.Actions.REFRESH, {
            newReportOfSickness: new ReportOfSickness(response.data),
            oldReportOfSickness: payload.reportOfSickness
          });
        } else {
          commit(ReportsOfSickness.Mutations.UPDATE, response.data);
        }
        dispatch(ReportsOfSickness.Actions.Recovery.Update.SUCCESS, response.data);
      })
      .catch(errors => dispatch(ReportsOfSickness.Actions.Recovery.Update.FAILED, errors));
  },

  [ReportsOfSickness.Actions.Recovery.Delete.START] ({ commit, dispatch }, payload) {
    RecoveryUtils.Delete(payload.reportOfSickness.id)
      .then((response) => {
        dispatch(ReportsOfSickness.Actions.Recovery.Delete.SUCCESS, payload);
        commit(ReportsOfSickness.Mutations.Recovery.REMOVE, payload);
      });
  },

  [ReportsOfSickness.Actions.UwvDecision.Create.START] ({ commit, dispatch }, payload) {
    UwvDecisionUtils.Create(payload.reportOfSickness.id, payload.uwvDecision)
      .then((response) => {
        dispatch(ReportsOfSickness.Actions.UwvDecision.Create.SUCCESS, response.data);
        commit(ReportsOfSickness.Mutations.UPDATE, response.data);
      })
      .catch((errors) => {
        console.log('UwvDecision.Create.FAILED', errors);
      });
  }
};

const mutations = {
  [ReportsOfSickness.Mutations.SET] (state, payload) {
    const reportsOfSicknessModels = payload.map(rs => {
      if (rs.status !== States.Removed) {
        return new ReportOfSickness(rs);
      }
    }).filter(Boolean);

    let result = {};
    reportsOfSicknessModels.forEach(reportOfSickness => {
      if (!result.hasOwnProperty(reportOfSickness.employeeId)) {
        result[reportOfSickness.employeeId] = [];
      }
      result[reportOfSickness.employeeId].push(reportOfSickness);
    });
    state.reportsOfSickness = result;
  },

  [ReportsOfSickness.Mutations.UPSERT] (state, payload) {
    const reportsOfSicknessModels = payload.data.map(rs => {
      return new ReportOfSickness(rs);
    });

    state.reportsOfSickness[payload.employeeId] = reportsOfSicknessModels;
  },

  [ReportsOfSickness.Mutations.ADD] (state, payload) {
    let reportOfSickness = payload;
    if (!(payload instanceof ReportOfSickness)) {
      reportOfSickness = new ReportOfSickness(payload);
    }

    if (!state.reportsOfSickness.hasOwnProperty(reportOfSickness.employeeId)) {
      state.reportsOfSickness[reportOfSickness.employeeId] = [];
    }

    state.reportsOfSickness[reportOfSickness.employeeId].push(reportOfSickness);
  },

  [ReportsOfSickness.Mutations.REMOVE] (state, payload) {
    if (payload != null) {
      let existingIndex = state.reportsOfSickness[payload.employeeId]
        .findIndex(r => r.id === payload.id);

      if (existingIndex != null) {
        state.reportsOfSickness[payload.employeeId]
          .splice(existingIndex, 1);
      }
    }
  },

  [ReportsOfSickness.Mutations.Period.REMOVE] (state, payload) {
    if (payload != null) {
      let existingIndex = payload.reportOfSickness.periods
        .findIndex(p => p.id === payload.period.id);

      if (existingIndex != null) {
        state.reportsOfSickness[payload.reportOfSickness.employeeId].forEach(r => {
          if (r.id === payload.reportOfSickness.id) {
            r.periods.splice(existingIndex, 1);
          }
        });
      }
    }
  },

  [ReportsOfSickness.Mutations.Recovery.REMOVE] (state, payload) {
    if (payload != null && payload.reportOfSickness != null) {
      state.reportsOfSickness[payload.reportOfSickness.employeeId].forEach(r => {
        if (r.id === payload.reportOfSickness.id) {
          r.recovery = null;
        }
      });
    }
  },

  [ReportsOfSickness.Mutations.UPDATE] (state, payload) {
    if (payload != null) {
      let reportOfSickness = new ReportOfSickness(payload);
      let existingIndex = state.reportsOfSickness[reportOfSickness.employeeId]
        .findIndex(r => r.id === reportOfSickness.id);

      if (existingIndex != null) {
        state.reportsOfSickness[reportOfSickness.employeeId]
          .splice(existingIndex, 1, reportOfSickness);
      }
    }
  },

  // to-do, make specific again
  [ReportsOfSickness.Mutations.IsLoading.CLEAR] (state) {
    state.reportsOfSicknessLoading = {
      'LOAD': false,
      'LOAD_ALL': false,
      'LOAD_FOR_EMPLOYEE': false,
      'CREATE': false,
      'UPDATE': false,
      'DELETE': false,
      'MARK_RECOVERED': false,
      'UNDO_MARK_RECOVERED': false,
      'PERIOD_CREATE': false,
      'PERIOD_DELETE': false,
      'PERIOD_UPDATE': false,
      'RECOVERY_CREATE': false,
      'RECOVERY_DELETE': false,
      'RECOVERY_UPDATE': false,
      'UWV_DECISION_CREATE': false
    };
  },

  [ReportsOfSickness.Mutations.IsLoading.SET] (state, payload) {
    state.reportsOfSicknessLoading[payload.type] = payload.value;
  },

  [App.Mutations.CLEAR] (state) {
    state.reportsOfSickness = {};
    state.reportsOfSicknessLoading = {
      'LOAD': false,
      'LOAD_ALL': false,
      'LOAD_FOR_EMPLOYEE': false,
      'CREATE': false,
      'UPDATE': false,
      'DELETE': false,
      'MARK_RECOVERED': false,
      'UNDO_MARK_RECOVERED': false,
      'PERIOD_CREATE': false,
      'PERIOD_DELETE': false,
      'PERIOD_UPDATE': false,
      'RECOVERY_CREATE': false,
      'RECOVERY_DELETE': false,
      'RECOVERY_UPDATE': false,
      'UWV_DECISION_CREATE': false
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
